import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api from "../services/api";
import { httpCacheBypass } from "../utils/httpCacheBypass";
import { useSocketStore } from "./socket";
import { useDeviceStore } from "./device";

// Debounce + in-flight dedupe caps the REST "thundering herd" described in
// Tasks/12-scalability-and-bottleneck-hardening.md (Phase 0 / TODO 0.1):
// every connected client used to independently call GET /api/votes/event/:id
// on every single vote_updated/song_performed/song_ended broadcast.
const REFRESH_DEBOUNCE_MS = 1500;
const REFRESH_JITTER_MS = 500;
// Ceiling on how long a continuous stream of broadcasts may keep postponing a
// refresh. A pure trailing debounce starves: during the busiest moment of a
// show, broadcasts arrive closer together than the debounce window, so the
// timer is reset forever and the refresh never fires — exactly when the data
// is changing fastest.
const REFRESH_MAX_WAIT_MS = 4000;

// Axios reports an aborted request through one of these; they are expected
// during teardown and must not be logged as failures.
const isAbortError = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError";

const createAbortController = () =>
  typeof AbortController === "undefined" ? null : new AbortController();

export const useVotesStore = defineStore("votes", () => {
  // State
  const votes = ref({});
  const userVotedSongs = ref({}); // Track which songs the user (device) has voted for
  const upNextSongIds = ref({});
  const queueEntries = ref({});
  const nowPlaying = ref({});
  const isLoading = ref(false);
  const currentEventId = ref(null);
  // Last-seen monotonic broadcast sequence per event (TODO 1.4/3.2).
  const lastSeqByEvent = ref({});
  // Fallback identity from TODO 2.5.1 (`seqSource`). A change of source is
  // treated as a new sequence domain and triggers a resync, same as a
  // backwards seq.
  const lastSeqSourceByEvent = ref({});

  // In-flight request de-duplication: concurrent callers for the same eventId
  // share one network request instead of firing one each.
  const inFlightFetches = new Map(); // eventId -> { promise, controller }
  // Trailing-debounce bookkeeping for socket-triggered refreshes.
  const pendingRefreshTimers = new Map(); // eventId -> { timer, firstScheduledAt }
  // Guards against an older, slower response overwriting newer state.
  const lastAppliedSequence = new Map(); // eventId -> number
  let fetchSequence = 0;

  // Getters
  const getEventVotes = computed(() => (eventId) => {
    return votes.value[eventId] || [];
  });

  const getSongVoteCount = computed(() => (eventId, songId) => {
    const eventVotes = votes.value[eventId] || [];
    const songVote = eventVotes.find((vote) => vote.songId === songId);
    return songVote ? songVote.totalVotes : 0;
  });

  const hasUserVotedForSong = computed(() => (eventId, songId) => {
    const eventVotedSongs = userVotedSongs.value[eventId] || [];
    return eventVotedSongs.includes(songId);
  });

  const getUpNextSongId = computed(() => (eventId) => {
    return upNextSongIds.value[eventId] || null;
  });

  const getQueueEntries = computed(() => (eventId) => {
    return queueEntries.value[eventId] || [];
  });

  const getNowPlaying = computed(() => (eventId) => {
    return nowPlaying.value[eventId] || null;
  });

  const applyEventVotePayload = (eventId, data) => {
    if (Array.isArray(data.queueEntries)) {
      queueEntries.value[eventId] = data.queueEntries;
      nowPlaying.value[eventId] = data.nowPlaying || null;
      votes.value[eventId] = [];
      upNextSongIds.value[eventId] = null;
      return;
    }

    votes.value[eventId] = data.results || [];
    upNextSongIds.value[eventId] = data.upNextSongId || null;
    queueEntries.value[eventId] = [];
    nowPlaying.value[eventId] = null;
  };

  // True when a realtime payload carries usable vote/queue state inline
  // (TODO 1.4/1.5), so the caller can apply it directly instead of
  // scheduling a REST refetch. Concert payloads carry `results` (voters
  // stripped — see TODO 1.4), karaoke payloads carry `queueEntries`.
  const hasEnrichedPayload = (data) =>
    Boolean(data) && (Array.isArray(data.queueEntries) || Array.isArray(data.results));

  // TODO 3.2: update the last-seen seq and report whether a targeted resync
  // is warranted. A skip ahead is a gap; a backwards jump (or seqSource
  // change) is never treated as a gap — instance restarts and the in-memory
  // fallback in 2.5.1 legitimately reset the counter.
  const trackSeq = (eventId, seq, seqSource) => {
    if (eventId === null || eventId === undefined || typeof seq !== "number") {
      return false;
    }

    const prev = lastSeqByEvent.value[eventId];
    const prevSource = lastSeqSourceByEvent.value[eventId];
    const nextSource = typeof seqSource === "string" ? seqSource : undefined;

    lastSeqByEvent.value[eventId] = seq;
    if (nextSource !== undefined) {
      lastSeqSourceByEvent.value[eventId] = nextSource;
    } else if (prevSource !== undefined) {
      delete lastSeqSourceByEvent.value[eventId];
    }

    if (prev === undefined) {
      return false;
    }
    if (prevSource !== nextSource) {
      return true;
    }
    if (seq < prev) {
      return true;
    }
    if (seq > prev + 1) {
      return true;
    }
    return false;
  };

  // Shared tail of every realtime handler below (TODO 1.4/1.5/1.6): apply an
  // enriched payload inline, or fall back to the debounced REST refetch so
  // older/degraded broadcasts still resync eventually.
  //
  // TODO 2.5.2: the monotonic guard in fetchEventVotesNow only ordered REST
  // responses *against each other* — an inline apply here never touched
  // lastAppliedSequence, so a REST request issued before this inline apply
  // (T1), which itself landed after an even newer broadcast was applied
  // inline (T2 > T1), could still resolve at T3 and pass the sequence check,
  // clobbering the newer socket state with pre-broadcast data. Claiming a
  // ticket here — the same counter fetchEventVotesNow uses — makes any
  // older in-flight REST response fail that check instead.
  //
  // TODO 3.2: even when the payload is enriched, a seq gap/backwards jump
  // schedules one debounced resync so missed intermediate state is recovered.
  const applyPayloadOrScheduleRefresh = (eventId, data, { needsResync = false } = {}) => {
    if (hasEnrichedPayload(data)) {
      lastAppliedSequence.set(eventId, ++fetchSequence);
      applyEventVotePayload(eventId, data);
      if (needsResync) {
        scheduleVotesRefresh(eventId);
      }
    } else {
      scheduleVotesRefresh(eventId);
    }
  };

  // Actions
  const submitVote = async (eventId, songId) => {
    try {
      isLoading.value = true;

      const deviceStore = useDeviceStore();
      const deviceId = await deviceStore.getDeviceId();

      const response = await api.post("/api/votes", {
        eventId,
        songId,
        deviceId // Include deviceId for anonymous voting
      });

      if (response.data.success) {
        // Forced: a request already in flight was issued before this vote was
        // recorded, so joining it would render the user's own vote missing.
        await refreshEventVotes(eventId);

        // Track that user has voted for this song
        if (!userVotedSongs.value[eventId]) {
          userVotedSongs.value[eventId] = [];
        }
        if (!userVotedSongs.value[eventId].includes(songId)) {
          userVotedSongs.value[eventId].push(songId);
        }

        // Emit socket event
        const socketStore = useSocketStore();
        socketStore.emit("submit_vote", { eventId, songId });

        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to submit vote"
      };
    } finally {
      isLoading.value = false;
    }
  };

  // The real network call, shared by the immediate and debounced paths.
  // Concurrent callers for the same eventId collapse into a single in-flight
  // request rather than issuing one per caller.
  //
  // `force` starts a fresh request even when one is already in flight. It is
  // required after a local write: the in-flight response was produced by a
  // query that ran *before* the write, so reusing it would show the operator
  // pre-write data (e.g. a song still listed as unperformed right after it
  // was marked performed).
  //
  // `bypassHttpCache` additionally skips the browser/CDN HTTP cache. The
  // endpoint is served as `public, max-age=2, stale-while-revalidate=10`, so
  // without it a refetch can legitimately be answered from cache with data up
  // to ~12s stale. Opt in only on single-client operator surfaces (the LED
  // display); audience phones must keep hitting the shared cache.
  const fetchEventVotesNow = (eventId, { force = false, bypassHttpCache = false } = {}) => {
    if (eventId === null || eventId === undefined || eventId === "") {
      return Promise.resolve();
    }

    const existing = inFlightFetches.get(eventId);
    if (existing && !force) {
      return existing.promise;
    }
    if (existing) {
      existing.controller?.abort();
      inFlightFetches.delete(eventId);
    }

    const sequence = ++fetchSequence;
    const controller = createAbortController();
    const entry = { controller };

    entry.promise = (async () => {
      try {
        const response = await api.get(
          `/api/votes/event/${eventId}`,
          httpCacheBypass(bypassHttpCache, controller ? { signal: controller.signal } : undefined)
        );

        // Drop out-of-order responses so a slow earlier request can never
        // clobber state a later one already applied.
        if (sequence < (lastAppliedSequence.get(eventId) || 0)) {
          return;
        }

        if (response.data.success) {
          lastAppliedSequence.set(eventId, sequence);
          applyEventVotePayload(eventId, response.data.data);
          currentEventId.value = eventId;
        }
      } catch (error) {
        if (!isAbortError(error)) {
          console.error("Failed to fetch event votes:", error);
        }
      } finally {
        // Only release the slot if it is still ours — a forced fetch may have
        // already replaced it, and blindly deleting would evict that newer
        // entry and silently disable de-duplication.
        if (inFlightFetches.get(eventId) === entry) {
          inFlightFetches.delete(eventId);
        }
      }
    })();

    inFlightFetches.set(eventId, entry);
    return entry.promise;
  };

  // Kept as the default export name so existing direct call sites (initial
  // mount load) are unaffected and stay immediate.
  const fetchEventVotes = (eventId) => fetchEventVotesNow(eventId);

  // Use after a local write, when joining an in-flight (pre-write) request
  // would return stale data.
  const refreshEventVotes = (eventId) => fetchEventVotesNow(eventId, { force: true });

  // Trailing debounce (with jitter) used exclusively by high-frequency socket
  // handlers below, so a burst of broadcasts for one event collapses into a
  // single request instead of one per broadcast. Jitter de-synchronises
  // concurrently connected clients so they don't all refetch at once, and
  // REFRESH_MAX_WAIT_MS guarantees the refresh still happens under a
  // sustained broadcast stream.
  const scheduleVotesRefresh = (eventId, { bypassHttpCache = false } = {}) => {
    if (eventId === null || eventId === undefined || eventId === "") {
      return;
    }

    const now = Date.now();
    const existing = pendingRefreshTimers.get(eventId);
    const firstScheduledAt = existing ? existing.firstScheduledAt : now;

    if (existing) {
      clearTimeout(existing.timer);
    }

    const remainingCap = Math.max(0, REFRESH_MAX_WAIT_MS - (now - firstScheduledAt));
    const delay = Math.min(REFRESH_DEBOUNCE_MS + Math.random() * REFRESH_JITTER_MS, remainingCap);

    // Sticky across coalesced calls: if any caller in this window asked for a
    // cache-bypassing read, the single request that replaces them all must
    // bypass too, otherwise the LED display silently loses its freshness
    // guarantee whenever a socket handler schedules first.
    const bypass = bypassHttpCache || Boolean(existing?.bypassHttpCache);

    const timer = setTimeout(() => {
      pendingRefreshTimers.delete(eventId);
      fetchEventVotesNow(eventId, { bypassHttpCache: bypass });
    }, delay);

    pendingRefreshTimers.set(eventId, { timer, firstScheduledAt, bypassHttpCache: bypass });
  };

  // Cancels any pending debounced refresh and aborts an in-flight request for
  // an event, so leaving the event page doesn't leak timers or write a
  // response into state belonging to a view that has already unmounted.
  const cancelPendingRefresh = (eventId) => {
    const pending = pendingRefreshTimers.get(eventId);
    if (pending) {
      clearTimeout(pending.timer);
      pendingRefreshTimers.delete(eventId);
    }

    const inFlight = inFlightFetches.get(eventId);
    if (inFlight) {
      // Abort rather than merely forgetting the entry: dropping the map entry
      // on its own leaves the request running and its result still lands in
      // the store.
      inFlight.controller?.abort();
      inFlightFetches.delete(eventId);
    }
  };

  const cancelAllPendingRefreshes = () => {
    pendingRefreshTimers.forEach((pending) => clearTimeout(pending.timer));
    pendingRefreshTimers.clear();
    inFlightFetches.forEach((entry) => entry.controller?.abort());
    inFlightFetches.clear();
    lastAppliedSequence.clear();
  };

  const fetchUserVotes = async (eventId) => {
    try {
      const deviceStore = useDeviceStore();
      const deviceId = await deviceStore.getDeviceId();

      const response = await api.get(`/api/votes/device/${eventId}`, {
        params: { deviceId }
      });

      if (response.data.success) {
        userVotedSongs.value[eventId] = response.data.data.votes || [];
      }
    } catch (error) {
      console.error("Failed to fetch user votes:", error);
    }
  };

  // Socket event handlers
  const handleVoteUpdated = async (data) => {
    try {
      const { eventId, seq, seqSource } = data || {};

      if (!eventId) {
        return;
      }
      const needsResync = trackSeq(eventId, seq, seqSource);
      applyPayloadOrScheduleRefresh(eventId, data, { needsResync });
    } catch (error) {
      console.error("Error handling vote update:", error);
    }
  };

  const handleSongPerformed = (data) => {
    const { eventId, upNextSongId, seq, seqSource } = data || {};
    if (!eventId) {
      return;
    }
    const needsResync = trackSeq(eventId, seq, seqSource);

    // Update upNextSongId immediately from socket event, in case the
    // enriched payload below doesn't carry one (e.g. karaoke).
    if (upNextSongId !== undefined) {
      upNextSongIds.value[eventId] = upNextSongId || null;
    }

    applyPayloadOrScheduleRefresh(eventId, data, { needsResync });
  };

  const handleSongEnded = (data) => {
    const { eventId, seq, seqSource } = data || {};
    if (!eventId) {
      return;
    }
    const needsResync = trackSeq(eventId, seq, seqSource);
    applyPayloadOrScheduleRefresh(eventId, data, { needsResync });
  };

  // `set_changed` (StartNextSet) draws from the SAME per-event seq counter as
  // the three handlers above (TODO 2.5.1) and carries the same enriched
  // `results` (TODO 1.5). Views own the set-index state, but the seq must be
  // tracked here too — otherwise every set change leaves a one-wide hole in
  // the client's view of the sequence and TODO 3.2 reports a phantom gap on
  // the very next broadcast, triggering a refetch it exists to avoid.
  const handleSetChanged = (data) => {
    const { eventId, seq, seqSource } = data || {};
    if (!eventId) {
      return;
    }
    const needsResync = trackSeq(eventId, seq, seqSource);
    applyPayloadOrScheduleRefresh(eventId, data, { needsResync });
  };

  const clearVotes = () => {
    // Drop queued/in-flight refreshes first, otherwise a debounced refetch
    // scheduled moments earlier lands after the reset and repopulates state.
    cancelAllPendingRefreshes();
    votes.value = {};
    currentEventId.value = null;
    lastSeqByEvent.value = {};
    lastSeqSourceByEvent.value = {};
  };

  return {
    votes,
    userVotedSongs,
    upNextSongIds,
    queueEntries,
    nowPlaying,
    isLoading,
    currentEventId,
    lastSeqByEvent,
    getEventVotes,
    getQueueEntries,
    getNowPlaying,
    getSongVoteCount,
    hasUserVotedForSong,
    getUpNextSongId,
    submitVote,
    fetchEventVotes,
    fetchEventVotesNow,
    scheduleVotesRefresh,
    fetchUserVotes,
    cancelPendingRefresh,
    handleVoteUpdated,
    handleSongPerformed,
    handleSongEnded,
    handleSetChanged,
    clearVotes
  };
});
